import { Action, Subject } from '@erp/authz';
import { ProductModule } from '@/hooks/organization-config/useOrganizationEnabledModules';
import { isBooleanFeatureFlagEnabled } from '@/integrations/feature-flags/feature-flags';
import paths from '@/paths';
import { createRoute, Outlet, redirect } from '@tanstack/react-router';
import { Route as rootRoute } from 'src/pages/__root';

const LANDING_PAGE_CANDIDATES: Array<{
  path: string;
  modulesAnyOf?: ProductModule[];
  modulesAllOf?: ProductModule[];
  permission?: { action: Action; subject: Subject };
}> = [
  {
    path: paths.authenticated.salesOrders,
    modulesAnyOf: [ProductModule.ORDER_MANAGEMENT],
    permission: { action: Action.Read, subject: Subject.SalesOrder },
  },
  {
    path: paths.authenticated.rebates.agreements,
    modulesAnyOf: [ProductModule.REBATES],
    permission: { action: Action.Read, subject: Subject.RebateAgreement },
  },
  {
    path: paths.authenticated.procurement.suppliers,
    modulesAnyOf: [ProductModule.PROCUREMENT],
    permission: { action: Action.Read, subject: Subject.Supplier },
  },
  {
    path: paths.authenticated.famousEDI,
    modulesAllOf: [
      ProductModule.FAMOUS_INTEGRATION,
      ProductModule.ORDERFUL_EDI,
    ],
  },
  {
    path: paths.authenticated.shipAndDebit.dashboard,
    modulesAnyOf: [ProductModule.SHIP_AND_DEBIT],
  },
  {
    path: paths.authenticated.accountsPayable.invoices,
    modulesAnyOf: [ProductModule.AP_INVOICING],
    permission: { action: Action.Read, subject: Subject.APInvoice },
  },
  {
    path: paths.authenticated.companies,
    modulesAnyOf: [ProductModule.REBATES],
    permission: { action: Action.Read, subject: Subject.Company },
  },
];

async function canAccessCandidate(
  candidate: (typeof LANDING_PAGE_CANDIDATES)[number],
  isModuleEnabled: (module: ProductModule) => boolean,
  authzClient: { can: (action: Action, subject: Subject) => Promise<boolean> },
): Promise<boolean> {
  if (
    candidate.modulesAnyOf &&
    !candidate.modulesAnyOf.some((m) => isModuleEnabled(m))
  ) {
    console.log(
      `[landing] SKIP ${candidate.path} — modulesAnyOf not met:`,
      candidate.modulesAnyOf.map((m) => `${m}=${isModuleEnabled(m)}`),
    );
    return false;
  }
  if (
    candidate.modulesAllOf &&
    !candidate.modulesAllOf.every((m) => isModuleEnabled(m))
  ) {
    console.log(
      `[landing] SKIP ${candidate.path} — modulesAllOf not met:`,
      candidate.modulesAllOf.map((m) => `${m}=${isModuleEnabled(m)}`),
    );
    return false;
  }
  if (candidate.permission) {
    const allowed = await authzClient.can(
      candidate.permission.action,
      candidate.permission.subject,
    );
    if (!allowed) {
      console.log(
        `[landing] SKIP ${candidate.path} — permission denied: ${candidate.permission.action} ${candidate.permission.subject}`,
      );
      return false;
    }
  }
  console.log(`[landing] OK ${candidate.path} — accessible`);
  return true;
}

export const Route = createRoute({
  path: '/',
  getParentRoute: () => rootRoute,
  beforeLoad: async (ctx) => {
    const { auth, isModuleEnabled, authzClient } = ctx.context;
    if (auth.isFetchingAuthInitialStatus) {
      return true;
    }

    if (auth.currentUser) {
      console.log('[landing] User authenticated, evaluating candidates...');
      console.log(
        '[landing] User:',
        auth.currentUser.id,
        auth.currentUser.name,
      );

      if (isBooleanFeatureFlagEnabled('inbox-enabled', false)) {
        console.log(
          '[landing] inbox-enabled flag is on — redirecting to /inbox',
        );
        throw redirect({ to: '/inbox' });
      }

      // Fall through ordered candidate list
      for (const candidate of LANDING_PAGE_CANDIDATES) {
        if (await canAccessCandidate(candidate, isModuleEnabled, authzClient)) {
          console.log(`[landing] REDIRECTING to ${candidate.path}`);
          throw redirect({ to: candidate.path });
        }
      }

      console.log('[landing] No candidates matched — redirecting to /welcome');
      throw redirect({ to: '/welcome' });
    } else {
      console.log('[landing] No currentUser — redirecting to /login');
      throw redirect({
        to: '/login',
        search: {
          redirect: ctx.location.href,
        },
      });
    }
  },
  component: () => <Outlet />,
});
